/* Boho V2 motion loops: short silent MP4 loops made only from real photographs (Google Flow,
 * Veo 3.1 Lite, first frame pinned to the photograph). Progressive enhancement only.
 *
 * Markup: <video data-bv2-loop data-bv2-src="…mp4" muted playsinline loop preload="none"
 *         aria-hidden="true"></video>, laid over an <img> that is the still. Add data-bv2-hero to
 *         the one loop that sits in the first viewport.
 * Guardrails (load budget, 2026-10-01):
 * - Nothing is fetched until the page's `load` event has fired, so the still (the LCP image)
 *   paints first. A data-bv2-hero loop also waits for its still to be complete and painted.
 * - No <source> is in the markup. One is attached (preload metadata) only when the video is within
 *   one viewport of the screen, prefers-reduced-motion is no-preference, and the site-wide pause
 *   switch (html.bv2-still / localStorage "bv2-motion", owned by boho-v2.js) is off.
 * - A loop plays only while it is actually on screen and pauses when it leaves.
 * - Phones (under 810 px wide): at most two videos play at once, counting every <video> on the
 *   page (the moment band's clips too). A loop does not start when two are already playing, and
 *   a loop yields (pauses) when another video starts and the count goes above two.
 * - The site-wide switch announces itself as the `bv2:motion` event; every loop pauses or resumes
 *   with it. The video stays transparent until it is playing, so the still is what shows first
 *   and what stays if anything fails.
 * - Tap to play (boho-v2.js): when motion may not autoplay, visitor plays (data-bv2-user) are its.
 */
(() => {
  if (window.bohoV2Loops) {
    window.bohoV2Loops.scan();
    return;
  }
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 809px)');
  const PHONE_MAX = 2;
  const switchPaused = () => {
    if (root.classList.contains('bv2-still')) return true;
    try {
      return window.localStorage.getItem('bv2-motion') === 'paused';
    } catch (e) {
      return false;
    }
  };
  const allowed = () => !reduce.matches && !switchPaused();
  let loaded = document.readyState === 'complete';
  const vids = new Set();
  const near = new WeakMap();
  const visible = new WeakMap();
  const ready = new WeakMap();

  function stillReady(v) {
    if (!v.hasAttribute('data-bv2-hero')) return true;
    if (ready.get(v)) return true;
    const img = v.parentElement && v.parentElement.querySelector('img');
    if (img && !(img.complete && img.naturalWidth > 0)) return false;
    // Two frames after the still is complete, so it has painted before any MP4 byte is asked for.
    if (!ready.has(v)) {
      ready.set(v, false);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          ready.set(v, true);
          sync(v);
        })
      );
    }
    return false;
  }

  function attach(v) {
    if (v.hasAttribute('data-bv2-loaded') || !v.dataset.bv2Src) return;
    const s = document.createElement('source');
    s.src = v.dataset.bv2Src;
    s.type = 'video/mp4';
    v.preload = 'metadata';
    v.appendChild(s);
    v.setAttribute('data-bv2-loaded', '');
    v.load();
  }

  const playingCount = () => {
    let n = 0;
    document.querySelectorAll('video').forEach((x) => {
      if (!x.paused && !x.ended) n += 1;
    });
    return n;
  };

  function sync(v) {
    const ok = loaded && allowed() && stillReady(v);
    if (ok && near.get(v)) attach(v);
    if (ok && visible.get(v)) {
      if (v.paused && !(phone.matches && playingCount() >= PHONE_MAX)) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    } else if (!v.paused && (allowed() || !v.hasAttribute('data-bv2-user'))) {
      v.pause();
    }
  }

  const syncAll = () => vids.forEach(sync);

  const mk = (map, margin) =>
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              map.set(e.target, e.isIntersecting);
              sync(e.target);
            }),
          { rootMargin: margin }
        )
      : null;
  const ioNear = mk(near, '100% 0px');
  const ioSeen = mk(visible, '0px');

  function scan() {
    document.querySelectorAll('video[data-bv2-loop]:not([data-bv2-seen])').forEach((v) => {
      v.setAttribute('data-bv2-seen', '');
      v.muted = true;
      vids.add(v);
      v.addEventListener('playing', () => v.setAttribute('data-bv2-playing', ''));
      // A loop that stops frees a phone slot for another one on screen.
      v.addEventListener('pause', () => {
        if (phone.matches) syncAll();
      });
      if (ioNear && ioSeen) {
        ioNear.observe(v);
        ioSeen.observe(v);
      } else {
        near.set(v, true);
        visible.set(v, true);
        sync(v);
      }
    });
    if (window.bohoV2TapScan) window.bohoV2TapScan();
  }

  // Any video starting on a phone: loops give way until at most two videos play.
  document.addEventListener(
    'play',
    (e) => {
      if (!phone.matches || !allowed()) return;
      vids.forEach((x) => {
        if (x !== e.target && !x.paused && playingCount() > PHONE_MAX) x.pause();
      });
    },
    true
  );
  document.addEventListener('bv2:motion', syncAll);
  reduce.addEventListener('change', syncAll);
  phone.addEventListener('change', syncAll);
  if (!loaded) {
    window.addEventListener('load', () => {
      loaded = true;
      syncAll();
    });
  }
  window.bohoV2Loops = { scan, syncAll };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  }
  scan();
})();
